/* Fonts load from public/index.html (preconnect + preload) — do not @import here. */

.landing-screen-root {
  position: relative;
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  min-height: 100dvh;
}

.landing-page {
  --green: #16a34a;
  --green-d: #15803d;
  --green-dk: #0b3d24;
  --ink: #0c1611;
  --bg: #fbfcfb;
  --surface: #ffffff;
  --muted: #5b6b61;
  --soft: #8a998f;
  --line: #eaefea;
  --ring: rgba(22, 163, 74, 0.14);
  --grad: linear-gradient(135deg, #1fb562, #15803d);
  --radius: 20px;
  --maxw: 1140px;
  --shadow-sm: 0 1px 2px rgba(12, 22, 17, 0.04), 0 4px 16px rgba(12, 22, 17, 0.04);
  --shadow-md: 0 8px 30px -12px rgba(12, 22, 17, 0.18);
  --shadow-lg: 0 40px 90px -40px rgba(11, 61, 36, 0.45);
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: var(--ink);
  background: var(--bg);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  letter-spacing: -0.005em;
  /* RN Web roots use overflow:hidden — fill the stack screen and scroll inside */
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  scroll-behavior: smooth;
}

.landing-page *,
.landing-page *::before,
.landing-page *::after {
  box-sizing: border-box;
}

.landing-page a {
  color: inherit;
  text-decoration: none;
}

.landing-page .wrap {
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 0 22px;
}

.landing-page h1,
.landing-page h2,
.landing-page h3 {
  font-family: 'Sora', 'Inter', sans-serif;
  letter-spacing: -0.025em;
  line-height: 1.08;
}

.landing-page .display {
  font-size: clamp(38px, 5.6vw, 64px);
  font-weight: 800;
}

.landing-page h2 {
  font-size: clamp(27px, 3.6vw, 40px);
  font-weight: 700;
}

.landing-page .sub {
  color: var(--muted);
  font-size: 17.5px;
  max-width: 600px;
}

.landing-page .eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--green-d);
  background: var(--surface);
  border: 1px solid var(--line);
  padding: 7px 14px;
  border-radius: 999px;
  box-shadow: var(--shadow-sm);
}

.landing-page .eyebrow .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--green);
  box-shadow: 0 0 0 4px var(--ring);
}

.landing-page .btn {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-weight: 600;
  font-size: 15px;
  border-radius: 13px;
  padding: 13px 22px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.18s cubic-bezier(0.2, 0.7, 0.3, 1), box-shadow 0.18s, background 0.18s;
  font-family: inherit;
}

.landing-page .btn svg {
  width: 17px;
  height: 17px;
}

.landing-page .btn-primary {
  background: var(--grad);
  color: #fff;
  box-shadow: 0 10px 24px -8px rgba(21, 128, 61, 0.55);
}

.landing-page .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 34px -10px rgba(21, 128, 61, 0.6);
}

.landing-page .btn-ghost {
  background: var(--surface);
  color: var(--ink);
  border-color: var(--line);
  box-shadow: var(--shadow-sm);
}

.landing-page .btn-ghost:hover {
  transform: translateY(-2px);
  border-color: #d6e2d9;
}

.landing-page section {
  padding: 96px 0;
}

.landing-page header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
  border-bottom: 1px solid #f0f0f0;
}

.landing-page .nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 84px;
}

.landing-page .brand {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Sora', sans-serif;
  font-weight: 800;
  font-size: 22px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: inherit;
}

.landing-page .brand-mark {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 72px;
  height: 72px;
}

.landing-page .brand-mark::before {
  content: '';
  position: absolute;
  width: 46px;
  height: 46px;
  border-radius: 11px;
  background: var(--grad);
  box-shadow: 0 5px 14px -6px rgba(21, 128, 61, 0.5);
}

.landing-page .brand-mark--compact {
  width: 40px;
  height: 40px;
}

.landing-page .brand-mark--compact::before {
  width: 30px;
  height: 30px;
  border-radius: 9px;
}

.landing-page .brand-name {
  font-family: 'Sora', sans-serif;
  font-weight: 800;
  font-size: 1.15em;
  letter-spacing: -0.02em;
  line-height: 1;
  display: flex;
  align-items: center;
  height: 72px;
}

.landing-page .brand-name-accent {
  background: var(--grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.landing-page .brand--footer {
  font-size: 17px;
}

.landing-page .brand--footer .brand-name {
  height: 40px;
}

.landing-page .brand-icon {
  position: relative;
  z-index: 1;
  display: block;
  width: 72px;
  height: 72px;
  object-fit: contain;
  border-radius: 9px;
  background: #ffffff;
}

.landing-page .brand-icon--compact {
  width: 40px;
  height: 40px;
}

.landing-page .play-badge {
  display: inline-flex;
  align-items: center;
  transition: transform 0.18s cubic-bezier(0.2, 0.7, 0.3, 1), opacity 0.18s;
  line-height: 0;
}

.landing-page .play-badge img {
  display: block;
  height: 48px;
  width: auto;
}

.landing-page .play-badge:hover {
  transform: translateY(-2px);
  opacity: 0.94;
}

.landing-page .play-badge--on-dark img {
  height: 48px;
}

.landing-page .final-cta-row {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  align-items: center;
  margin-top: 30px;
}

.landing-page .final-cta-row .btn-primary {
  margin-top: 0;
  min-height: 48px;
  padding: 11px 22px;
  box-sizing: border-box;
}

.landing-page .final-cta-row .play-badge {
  height: 48px;
  display: inline-flex;
  align-items: center;
}

.landing-page .final-cta-row .play-badge img {
  height: 48px;
  width: auto;
}

.landing-page .nav-links {
  display: flex;
  gap: 30px;
  align-items: center;
}

.landing-page .nav-links a:not(.btn),
.landing-page .nav-links button:not(.btn) {
  color: var(--muted);
  font-weight: 500;
  font-size: 15px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
}

.landing-page .nav-links a:not(.btn):hover,
.landing-page .nav-links button:not(.btn):hover {
  color: var(--ink);
}

.landing-page .hero {
  position: relative;
  padding: 36px 0 56px;
  overflow: hidden;
}

.landing-page .hero::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  background:
    radial-gradient(680px 420px at 78% 18%, rgba(31, 181, 98, 0.12), transparent 60%),
    radial-gradient(520px 360px at 8% 88%, rgba(31, 181, 98, 0.08), transparent 60%);
}

.landing-page .hero-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1.06fr 0.94fr;
  gap: 56px;
  align-items: start;
}

.landing-page .hero h1 {
  margin: 22px 0 18px;
}

.landing-page .hero h1 .hl {
  background: var(--grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.landing-page .hero p {
  font-size: 18.5px;
  color: var(--muted);
  max-width: 520px;
}

.landing-page .cta-row {
  display: flex;
  gap: 13px;
  flex-wrap: wrap;
  margin-top: 30px;
}

.landing-page .mini {
  margin-top: 22px;
  display: flex;
  gap: 22px;
  flex-wrap: wrap;
  color: var(--muted);
  font-size: 14px;
}

.landing-page .mini span {
  display: flex;
  align-items: center;
  gap: 7px;
}

.landing-page .mini b {
  color: var(--ink);
  font-weight: 600;
}

.landing-page .tick {
  color: var(--green);
}

.landing-page .phone-wrap {
  position: relative;
  justify-self: center;
  align-self: center;
}

.landing-page .glow {
  position: absolute;
  inset: -30px;
  background: radial-gradient(circle at 50% 40%, rgba(31, 181, 98, 0.22), transparent 65%);
  filter: blur(8px);
  z-index: 0;
}

.landing-page .phone {
  position: relative;
  z-index: 1;
  width: 300px;
  height: 610px;
  background: #0c0c0c;
  border-radius: 46px;
  padding: 11px;
  box-shadow: var(--shadow-lg);
}

.landing-page .app-showcase {
  position: relative;
  z-index: 1;
  width: 100%;
  margin: 0;
  padding: 0 0 8px;
}

.landing-page .showcase-section {
  padding: 56px 0 24px;
}

.landing-page .showcase-section .head {
  margin-bottom: 28px;
}

.landing-page .showcase-shell {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 12px;
}

.landing-page .showcase-viewport {
  flex: 1;
  overflow: hidden;
  touch-action: pan-y;
  cursor: grab;
  min-height: 340px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.landing-page .showcase-viewport.is-ready {
  opacity: 1;
}

.landing-page .showcase-viewport:active {
  cursor: grabbing;
}

.landing-page .showcase-track {
  display: flex;
  gap: 24px;
  will-change: transform;
}

.landing-page .showcase-card {
  flex: 0 0 min(920px, calc(100vw - 120px));
  display: grid;
  grid-template-columns: minmax(220px, 0.95fr) minmax(220px, 1.05fr);
  gap: 28px;
  align-items: center;
  padding: 28px 32px;
  border-radius: 28px;
  background: #fff8f1;
  border: 1px solid #f1e6d8;
  box-shadow: 0 18px 50px -28px rgba(12, 22, 17, 0.22);
  opacity: 0.5;
  transform: scale(0.94);
}

.landing-page .showcase-card.is-active {
  opacity: 1;
  transform: scale(1);
  box-shadow: 0 28px 70px -32px rgba(12, 22, 17, 0.28);
}

.landing-page .showcase-phone {
  display: flex;
  justify-content: center;
  align-items: center;
}

.landing-page .showcase-phone-bezel {
  width: min(250px, 100%);
  aspect-ratio: 9 / 19.5;
  padding: 10px;
  border-radius: 34px;
  background: #101010;
  box-shadow: 0 20px 40px -24px rgba(0, 0, 0, 0.55);
  overflow: hidden;
}

.landing-page .showcase-phone-bezel img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  border-radius: 26px;
  display: block;
  user-select: none;
  -webkit-user-drag: none;
  background: #101010;
}

.landing-page .showcase-copy h3 {
  font-family: 'Sora', sans-serif;
  font-size: clamp(1.35rem, 2.4vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0 0 14px;
  color: var(--ink);
}

.landing-page .showcase-copy p {
  margin: 0;
  font-size: clamp(1rem, 1.5vw, 1.125rem);
  line-height: 1.65;
  color: var(--muted);
  max-width: 38ch;
}

.landing-page .showcase-arrow {
  flex: 0 0 auto;
  width: 46px;
  height: 46px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #fff;
  color: var(--ink);
  display: grid;
  place-items: center;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.landing-page .showcase-arrow svg {
  width: 22px;
  height: 22px;
  stroke: currentColor;
}

.landing-page .showcase-arrow:hover {
  transform: translateY(-1px);
  border-color: #d7e5da;
  background: #f7fcf8;
}

.landing-page .showcase-arrow:disabled {
  opacity: 0.35;
  cursor: default;
}

.landing-page .showcase-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 22px;
}

.landing-page .showcase-dots button {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: #d8dde2;
  cursor: pointer;
  transition: width 0.25s ease, background 0.25s ease;
}

.landing-page .showcase-dots button.on {
  width: 28px;
  background: var(--green);
}

.landing-page .screen {
  background: #0c0c0c;
  border-radius: 36px;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.landing-page .phone-screen-shot {
  padding: 0;
  background: #000;
}

.landing-page .phone-shot-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 78%;
  display: block;
  border-radius: 36px;
}

.landing-page .phone-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 14px;
  position: relative;
  z-index: 2;
}

.landing-page .phone-dots button {
  width: 7px;
  height: 7px;
  border: none;
  border-radius: 999px;
  padding: 0;
  background: rgba(12, 22, 17, 0.18);
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;
}

.landing-page .phone-dots button.on {
  background: var(--green);
  transform: scale(1.15);
}

.landing-page .shot-gallery-head {
  text-align: center;
  margin: 48px auto 22px;
  max-width: 520px;
}

.landing-page .shot-gallery-title {
  font-family: 'Sora', sans-serif;
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 10px 0 0;
  color: var(--ink);
}

.landing-page .shot-gallery {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 8px 4px 20px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

.landing-page .shot-gallery::-webkit-scrollbar {
  height: 6px;
}

.landing-page .shot-gallery::-webkit-scrollbar-thumb {
  background: rgba(12, 22, 17, 0.15);
  border-radius: 999px;
}

.landing-page .shot-card {
  flex: 0 0 auto;
  width: min(220px, 42vw);
  margin: 0;
  scroll-snap-align: start;
}

.landing-page .shot-card img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 22px;
  border: 1px solid var(--line);
  box-shadow: var(--shadow-md);
  background: #111;
}

.landing-page .s-top {
  padding: 26px 20px 8px;
}

.landing-page .s-hi {
  font-size: 13px;
  color: var(--soft);
  font-weight: 500;
}

.landing-page .s-q {
  font-family: 'Sora', sans-serif;
  font-weight: 700;
  font-size: 20px;
  margin-top: 3px;
  letter-spacing: -0.02em;
}

.landing-page .s-card {
  margin: 14px 16px 6px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 20px;
  padding: 16px;
  box-shadow: var(--shadow-md);
}

.landing-page .s-tag {
  font-size: 11px;
  font-weight: 700;
  color: var(--green-d);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.landing-page .s-dish {
  font-family: 'Sora', sans-serif;
  font-weight: 700;
  font-size: 18px;
  margin: 8px 0 4px;
}

.landing-page .s-meta {
  font-size: 12.5px;
  color: var(--soft);
}

.landing-page .s-pills {
  display: flex;
  gap: 6px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.landing-page .s-pill {
  font-size: 11px;
  background: var(--ring);
  color: var(--green-dk);
  padding: 5px 10px;
  border-radius: 999px;
  font-weight: 600;
}

.landing-page .s-list {
  padding: 8px 16px;
}

.landing-page .s-row {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 0;
  border-bottom: 1px solid var(--line);
}

.landing-page .s-row:last-child {
  border-bottom: 0;
}

.landing-page .s-dot {
  width: 34px;
  height: 34px;
  border-radius: 11px;
  background: var(--ring);
  display: grid;
  place-items: center;
}

.landing-page .s-dot svg {
  width: 18px;
  height: 18px;
  stroke: var(--green-d);
}

.landing-page .s-rt {
  flex: 1;
}

.landing-page .s-rn {
  font-size: 14px;
  font-weight: 600;
}

.landing-page .s-rs {
  font-size: 11.5px;
  font-weight: 600;
}

.landing-page .warn {
  color: #c2410c;
}

.landing-page .ok {
  color: var(--green);
}

.landing-page .s-tab {
  margin-top: auto;
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 14px 0 18px;
  border-top: 1px solid var(--line);
  background: #fff;
}

.landing-page .s-tab i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #dde5df;
}

.landing-page .s-tab i.on {
  background: var(--green);
  width: 22px;
  border-radius: 4px;
}

.landing-page .stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: var(--line);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  overflow: hidden;
  margin-top: 64px;
}

.landing-page .stat {
  background: var(--surface);
  text-align: center;
  padding: 28px 16px;
}

.landing-page .stat b {
  font-family: 'Sora', sans-serif;
  display: block;
  font-size: 30px;
  font-weight: 800;
  color: var(--green-dk);
}

.landing-page .stat span {
  font-size: 13.5px;
  color: var(--muted);
}

.landing-page .head {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-bottom: 54px;
}

.landing-page .head .sub {
  text-align: center;
}

.landing-page .prob-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 10px;
}

.landing-page .prob {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 26px;
  box-shadow: var(--shadow-sm);
}

.landing-page .prob .ic {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fff4ed;
  display: grid;
  place-items: center;
}

.landing-page .prob .ic svg {
  width: 22px;
  height: 22px;
  stroke: #ea580c;
}

.landing-page .prob h3 {
  font-size: 18px;
  margin: 16px 0 7px;
  font-weight: 700;
}

.landing-page .prob p {
  font-size: 14.5px;
  color: var(--muted);
}

.landing-page .feat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.landing-page .feat {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 28px;
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
}

.landing-page .feat:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-md);
  border-color: #d8e5db;
}

.landing-page .feat .ic {
  width: 50px;
  height: 50px;
  border-radius: 14px;
  background: var(--ring);
  display: grid;
  place-items: center;
}

.landing-page .feat .ic svg {
  width: 24px;
  height: 24px;
  stroke: var(--green-d);
}

.landing-page .feat h3 {
  font-size: 18.5px;
  margin: 18px 0 8px;
  font-weight: 700;
}

.landing-page .feat p {
  font-size: 14.5px;
  color: var(--muted);
}

.landing-page .how {
  background: var(--green-dk);
  color: #fff;
  border-radius: 32px;
  margin: 0 22px;
  padding: 88px 0;
  --setup-step-phone-height: 340px;
}

.landing-page .how .wrap {
  max-width: 1040px;
}

.landing-page .how .eyebrow {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.16);
  color: #cfead9;
}

.landing-page .how h2 {
  color: #fff;
}

.landing-page .how .sub {
  color: rgba(255, 255, 255, 0.74);
}

.landing-page .steps {
  display: grid;
  grid-template-columns: 1fr 1fr 2fr;
  gap: 28px;
  margin-top: 50px;
}

.landing-page .step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.landing-page .step-phone {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 22px;
}

.landing-page .step-phone-dual {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 22px;
  padding: 0 10px;
}

.landing-page .step-phone-dual .step-phone {
  margin-bottom: 0;
  width: auto;
  flex: 0 0 auto;
}

.landing-page .step-phone-dual .step-phone-bezel {
  width: 100%;
}

.landing-page .step-phone-or {
  font-family: 'Sora', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.86);
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  padding: 6px 8px;
}

.landing-page .step-phone-bezel {
  height: var(--setup-step-phone-height);
  width: calc(var(--setup-step-phone-height) * 9 / 19.5);
  max-width: 100%;
  padding: 6px;
  border-radius: 26px;
  background: #f3f4f6;
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 18px 36px -22px rgba(0, 0, 0, 0.65);
  overflow: hidden;
}

.landing-page .step-phone-bezel img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  border-radius: 20px;
  display: block;
  background: transparent;
  user-select: none;
  -webkit-user-drag: none;
}

.landing-page .step .n {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  display: grid;
  place-items: center;
  font-family: 'Sora', sans-serif;
  font-weight: 700;
  font-size: 17px;
  color: #fff;
}

.landing-page .step h3 {
  margin: 16px 0 8px;
  font-size: 19px;
  font-weight: 700;
}

.landing-page .step p {
  color: rgba(255, 255, 255, 0.74);
  font-size: 14.5px;
  max-width: 28ch;
  margin: 0 auto;
}

.landing-page .price-track {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
  align-items: stretch;
}

.landing-page .plan {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 24px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-sm);
}

.landing-page .plan.pop {
  border: 1.5px solid var(--green);
  box-shadow: var(--shadow-md);
  position: relative;
}

.landing-page .plan .badge {
  position: absolute;
  top: -13px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--grad);
  color: #fff;
  font-size: 11.5px;
  font-weight: 700;
  padding: 6px 15px;
  border-radius: 999px;
  box-shadow: 0 6px 14px -6px rgba(21, 128, 61, 0.6);
}

.landing-page .plan h3 {
  font-size: 19px;
  font-weight: 700;
}

.landing-page .plan .why {
  font-size: 13.5px;
  color: var(--soft);
  margin-top: 3px;
}

.landing-page .plan .amt {
  font-family: 'Sora', sans-serif;
  font-size: 38px;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 16px 0 0;
}

.landing-page .plan .amt small {
  font-size: 14px;
  color: var(--muted);
  font-weight: 500;
  font-family: 'Inter', sans-serif;
}

.landing-page .plan ul {
  list-style: none;
  margin: 22px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  padding: 0;
}

.landing-page .plan li {
  font-size: 14.5px;
  color: #384a40;
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.landing-page .plan li svg {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  stroke: var(--green);
  margin-top: 1px;
}

.landing-page .plan .btn {
  justify-content: center;
}

.landing-page .billing {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 5px;
  box-shadow: var(--shadow-sm);
}

.landing-page .billing .bt {
  border: 0;
  background: transparent;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: var(--muted);
  padding: 9px 18px;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: 0.18s;
}

.landing-page .billing .bt.active {
  background: var(--grad);
  color: #fff;
  box-shadow: 0 6px 14px -7px rgba(21, 128, 61, 0.7);
}

.landing-page .billing .bt em {
  font-style: normal;
  font-size: 11px;
  font-weight: 700;
  background: var(--ring);
  color: var(--green-dk);
  padding: 2px 7px;
  border-radius: 999px;
}

.landing-page .billing .bt.active em {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}

.landing-page .billed {
  font-size: 12.5px;
  color: var(--soft);
  min-height: 18px;
  margin-top: 2px;
}

.landing-page .cnav {
  display: none;
}

.landing-page .final-card {
  position: relative;
  overflow: hidden;
  background: var(--green-dk);
  border-radius: 32px;
  padding: 80px 28px;
  text-align: center;
  color: #fff;
}

.landing-page .final-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(600px 300px at 50% -10%, rgba(31, 181, 98, 0.4), transparent 60%);
}

.landing-page .final-card > * {
  position: relative;
  z-index: 1;
}

.landing-page .final-card h2 {
  color: #fff;
}

.landing-page .final-card p {
  color: rgba(255, 255, 255, 0.82);
  max-width: 500px;
  margin: 16px auto 0;
}

.landing-page .final .btn-primary {
  background: #fff;
  color: var(--green-dk);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
}

.landing-page .final small {
  display: block;
  margin-top: 16px;
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
}

.landing-page .final small a {
  color: rgba(255, 255, 255, 0.88);
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.18s;
}

.landing-page .final small a:hover {
  color: #fff;
}

.landing-page footer {
  padding: 40px 0;
  color: var(--muted);
  font-size: 14px;
  border-top: 1px solid var(--line);
  margin-top: 40px;
}

.landing-page .foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.landing-page .foot a:hover,
.landing-page .foot button:hover {
  color: var(--ink);
}

.landing-page .foot button {
  background: none;
  border: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
  padding: 0;
}

@media (max-width: 880px) {
  .landing-page .hero {
    padding-top: 28px;
  }

  .landing-page .hero-grid {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .landing-page .showcase-card {
    grid-template-columns: 1fr;
    flex-basis: min(680px, calc(100vw - 88px));
    padding: 22px 20px;
    text-align: center;
  }

  .landing-page .showcase-copy p {
    margin: 0 auto;
  }

  .landing-page .showcase-arrow {
    width: 40px;
    height: 40px;
  }

  .landing-page .phone-wrap {
    align-self: center;
    margin-top: 8px;
  }

  .landing-page .hero p {
    margin: 0 auto;
  }

  .landing-page .cta-row,
  .landing-page .mini {
    justify-content: center;
  }
}

@media (max-width: 560px) {
  .landing-page .showcase-shell {
    padding: 0 4px;
  }

  .landing-page .showcase-arrow {
    display: none;
  }

  .landing-page .showcase-card {
    flex-basis: calc(100vw - 32px);
    gap: 18px;
  }

  .landing-page .showcase-phone-bezel {
    width: min(210px, 72vw);
  }
}

@media (max-width: 760px) {
  .landing-page .nav-links a:not(.btn),
  .landing-page .nav-links button:not(.btn) {
    display: none;
  }

  .landing-page .prob-grid {
    grid-template-columns: 1fr;
  }

  .landing-page .how {
    margin: 0 14px;
    padding: 64px 0;
  }

  .landing-page .steps {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .landing-page .step-phone {
    margin-bottom: 18px;
  }

  .landing-page .step-phone-bezel {
    height: 320px;
    width: calc(320px * 9 / 19.5);
  }

  .landing-page .step-phone-dual {
    flex-direction: row;
    gap: 8px;
    padding: 0;
    justify-content: space-between;
  }

  .landing-page .step-phone-dual .step-phone {
    width: auto;
  }

  .landing-page .step-phone-or {
    font-size: 10px;
    padding: 5px 7px;
  }
}

@media (max-width: 560px) {
  .landing-page .step-phone-dual {
    flex-direction: row;
    justify-content: space-between;
    gap: 7px;
  }

  .landing-page .step-phone-dual .step-phone {
    width: auto;
  }

  .landing-page .step-phone-bezel {
    height: 240px;
    width: calc(240px * 9 / 19.5);
  }

  .landing-page .step-phone-or {
    font-size: 9px;
    padding: 4px 6px;
  }
}

@media (max-width: 640px) {
  .landing-page .stats {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 900px) {
  .landing-page .feat-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 600px) {
  .landing-page .feat-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 860px) {
  .landing-page .carousel {
    margin: 0 -22px;
    padding: 4px 0;
  }

  .landing-page .price-track {
    display: flex;
    gap: 16px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    padding: 6px 22px 8px;
    scrollbar-width: none;
  }

  .landing-page .price-track::-webkit-scrollbar {
    display: none;
  }

  .landing-page .price-track .plan {
    min-width: 84%;
    scroll-snap-align: center;
  }

  .landing-page .cnav {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
  }

  .landing-page .arrow {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid var(--line);
    background: var(--surface);
    box-shadow: var(--shadow-sm);
    font-size: 20px;
    line-height: 1;
    color: var(--green-d);
    cursor: pointer;
    display: grid;
    place-items: center;
    transition: 0.15s;
  }

  .landing-page .arrow:hover {
    transform: translateY(-2px);
  }

  .landing-page .arrow:disabled {
    opacity: 0.35;
    cursor: default;
    transform: none;
  }

  .landing-page .dots {
    display: flex;
    gap: 8px;
  }

  .landing-page .dots i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d7e0d9;
    transition: 0.2s;
    cursor: pointer;
  }

  .landing-page .dots i.on {
    background: var(--green);
    width: 22px;
    border-radius: 4px;
  }
}
